import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Upload,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import Link from 'antd/lib/typography/Link';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { read, utils } from 'xlsx';
import {
  EmailInvitation,
  emailInvitationStatusKorean,
} from '../../modules/auth';
import { styledTable } from '../styled/mixin';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface PartnerInvitationListComponentProps {
  partnerInvitations: EmailInvitation[];
  sendEmailInvitation: (selectedValues: number[]) => void;
  bulkCreateEmailInvitation: (dto: any) => void;
  deleteEmailInvitations: (selectedValues: number[]) => void;
}

function PartnerInvitationListComponent({
  partnerInvitations,
  sendEmailInvitation,
  bulkCreateEmailInvitation,
  deleteEmailInvitations,
}: PartnerInvitationListComponentProps) {
  useEffect(() => {
    setSelectedValues([]);
  }, []);

  const [selectedValues, setSelectedValues] = useState<any>([]);
  const columns: ColumnsType<any> = [
    {
      title: '회사명',
      dataIndex: 'companyName',
    },
    {
      title: '담당자명',
      dataIndex: 'userName',
    },
    {
      title: '이메일',
      dataIndex: 'email',
    },
    {
      title: '휴대전화번호',
      dataIndex: 'phoneNumber',
    },
    {
      title: '상태',
      dataIndex: 'status',
      render: (text: any, record: EmailInvitation) =>
        emailInvitationStatusKorean[record.status],
    },
    {
      title: '발송자',
      dataIndex: ['sender', 'name'],
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedValues([...selectedRowKeys]);
    },
    selectedRowKeys: selectedValues,
  };

  const beforeUpload = (file: any) => {
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const fileData = e.target.result;
      const wb = await read(fileData, { type: 'binary' });
      const sheetNames = await wb.SheetNames;
      const results: any = {
        data: [],
        errors: [],
      };

      for (let i = 0; i < sheetNames.length; i++) {
        const sheet = await wb.Sheets[sheetNames[i]];
        // if (sheetNames[i] === '예시') continue;
        const sheetRows: any[] = await utils.sheet_to_json(sheet);
        let index = 0;
        for (const row of sheetRows) {
          const companyName = row['회사명'];
          const userName = row['이름'];
          const email = row['이메일'];
          const phoneNumber = row['휴대전화번호'];

          if (
            typeof companyName !== 'string' ||
            typeof userName !== 'string' ||
            typeof email !== 'string' ||
            typeof phoneNumber !== 'string'
          ) {
            results.errors.push({
              sheetName: sheetNames[i],
              index,
            });
          } else {
            results.data.push({
              companyName,
              userName,
              email,
              phoneNumber,
            });
          }
          index += 1;
        }
      }

      if (results.errors.length > 0) {
        Modal.error({
          content: '업로드 파일이 잘못 되었습니다.',
        });
        return;
      }

      if (results.data.length === 0) {
        Modal.error({
          content: '업로드 파일의 내용이 없습니다.',
        });
        return;
      }
      bulkCreateEmailInvitation(results.data);
    };
    reader.readAsBinaryString(file);
    return false;
  };

  return (
    <StyledDiv>
      <Row justify="space-between">
        <Col>
          <h3>거래처 초대하기</h3>
        </Col>
        <Col>
          <Space size="small">
            <Link href="https://constlink-sample-files-bucket.s3.ap-northeast-2.amazonaws.com/partner_list_sample.xlsx">
              샘플파일
            </Link>
            <Upload
              accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              showUploadList={false}
              beforeUpload={beforeUpload}
            >
              <Button size="small">
                <UploadOutlined rev={undefined} />
                업로드
              </Button>
            </Upload>

            <Button
              onClick={async () => {
                const _selectedValues: number[] = [...selectedValues];
                setSelectedValues([]);
                await sendEmailInvitation(_selectedValues);
              }}
              disabled={!(selectedValues.length > 0)}
              size="small"
            >
              메일/문자 발송
            </Button>
            <div onClick={(e) => e.stopPropagation()}>
              <Popconfirm
                title="정말 삭제하시겠습니까?"
                onConfirm={async () => {
                  const _selectedValues: number[] = [...selectedValues];
                  setSelectedValues([]);
                  await deleteEmailInvitations(_selectedValues);
                }}
              >
                <Button
                  disabled={!(selectedValues.length > 0)}
                  danger
                  size="small"
                >
                  삭제
                </Button>
              </Popconfirm>
            </div>
          </Space>
        </Col>
      </Row>

      <Table<any>
        rowKey="id"
        columns={columns}
        dataSource={partnerInvitations}
        rowSelection={{ ...rowSelection, checkStrictly: false }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
          position: ['bottomCenter'],
        }}
      />
    </StyledDiv>
  );
}

export default PartnerInvitationListComponent;
